import { Box, Stack } from '@mui/material'
import {
    ArrowUpRightIcon,
    BuildingOfficeIcon,
    CalendarDotIcon,
    DownloadSimpleIcon,
    FilePdfIcon,
    FilesIcon,
    HighlighterIcon,
    MicrosoftExcelLogoIcon,
} from '@phosphor-icons/react'
import {
    AppraiserAssignment,
    assembleAddressFormat,
    Breadcrumbs,
    ContentChip,
    ContentChipWithOptions,
    Footer,
    isNotNullOrUndefined,
    isoDateStringToLocalizedMonthYear,
    isoDateStringToLocalFormat,
    LoadingSkeleton,
    NavTab,
    PageGridContainer,
    PageGridItem,
    TabBar,
    TinyNavButton,
    TitleImage,
    tokens,
    UserLinkList,
} from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { generatePath, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useValuationPageLayout } from './ValuationPageLayout.data'
import ValuationPageLayoutLoading from './ValuationPageLayout.load'
import { TextNavButtonNew } from 'ComponentLibrary/src/piazza/buttons'
import { ExportActionButton } from 'ComponentLibrary/src/piazza/buttons/ExportActionButton/ExportActionButton'
import { TextWithEllipsis } from 'ComponentLibrary/src/piazza/text'
import { getLatestUrlParams } from 'ComponentLibrary/src/hooks/useUrlState'
import { DYNAMIC_APP_PATHS } from 'ClientPortal/src/router/constants'
import { useDownloadValXml } from 'ClientPortal/src/api/Queries/valuations'
import { useState } from 'react'
import { ValXmlDownloadModal } from 'ClientPortal/src/common/modals/ValXmlDownloadModal'
import { ValuationPageNavButtonsLoad } from 'ClientPortal/src/pages/Assets/AssetDetails/Valuation/ValuationLayout/ValuationPageNavButtons.load'
import { useTranslation } from 'react-i18next'
import { useUserLinkCopy } from 'ClientPortal/src/i18n/useUserLinkCopy'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

export default function ValuationPageLayout() {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tActions } = useTranslation('terminology', { keyPrefix: 'common.actions' })
    const { t: tExport } = useTranslation('terminology', { keyPrefix: 'export' })
    const { t: tValuationPageLayout } = useTranslation('pages', { keyPrefix: 'assets.assetDetails.valuation.valuationPageLayout' })
    const locale = useCurrentResolvedLocale()
    const {
        valuation,
        valuationStatus,
        showPlausibility,
        asset,
        order,
        assetId,
        appraiserUsers,
        isValuationPageLayoutPending,
        assignedCustomerUsers,
        selectedTab,
        downloadReport,
        isDownloadingReport,
        valuationOptions,
        isOrderInitialLoading,
        isCustomerUsersPending,
        isValuationsPending,
        isComparablesEnabled,
    } = useValuationPageLayout()

    const { pathname } = useLocation()
    const navigate = useNavigate()
    const { downloadValXml, isDownloadingValXml } = useDownloadValXml()
    const [isValXmlModalOpen, setIsValXmlModalOpen] = useState(false)
    const userLinkCopy = useUserLinkCopy()

    // When we can't find a valuation, we redirect to the valuations list page of the same asset
    if (!isValuationPageLayoutPending && asset && !valuation) {
        void navigate(`/assets/${assetId}/valuations`)
        return null
    }

    const navigateToNewValuationPage = (newId: string) => {
        const newPath = location.pathname.replace(`/${valuation?.uuid}`, `/${newId}`)
        void navigate(newPath)
    }

    const { street, house_number } = asset?.address ?? {}
    const addressLine = assembleAddressFormat({ street, house_number }, { delimiter: ', ' })
    const valuationDate = isoDateStringToLocalFormat(valuation?.valuation_date, false, locale)
    const valuationMonthAndYear = isoDateStringToLocalizedMonthYear(valuation?.valuation_date, 'numeric', locale)
    const valuationTitle = `${tCommon('valuationReport')} ${valuationMonthAndYear}`
    const customerAssetReference = asset?.customer_asset_reference ? `[${asset?.customer_asset_reference}]` : undefined

    const breadCrumbItems = [
        { title: tCommon('allAssets'), link: '/assets', params: getLatestUrlParams('client-asset-filter') },
        { title: tCommon('asset'), link: `/assets/${assetId}/overview` },
        { title: tCommon('valuation') },
    ]
    const breadcrumbs = <Breadcrumbs items={breadCrumbItems} currentLocation={pathname} />
    const draftTitle = valuation?.is_draft
        ? (valuationOptions.find((option) => option.valuationId === valuation.uuid)?.valuationVersion ?? tCommon('draft'))
        : undefined

    if (isValuationPageLayoutPending) {
        return <ValuationPageLayoutLoading breadcrumbs={breadcrumbs} />
    }

    const titleImageSrc = asset?.title_image_thumbnail ?? asset?.title_image
    const shouldShowPlausibilityButton = showPlausibility && valuation?.is_draft && valuationStatus === 'in_review'
    const isNewestDraft = valuationOptions.length !== 0 && valuation?.uuid === valuationOptions[0].valuationId
    const shouldShowOrderButton = order?.status !== 'temp_hide_in_portal'
    const headerNavButtonCount = 1 + (shouldShowPlausibilityButton ? 1 : 0) + (shouldShowOrderButton ? 1 : 0)
    const isHeaderNavButtonsLoading = isOrderInitialLoading || (valuation?.is_draft && isValuationsPending)
    const headerActionGap = isHeaderNavButtonsLoading || headerNavButtonCount > 2 ? tokens.SizingSpacing400 : tokens.SizingSpacing800
    const headerNavButtonMinHeight = headerNavButtonCount > 2 ? tokens.SizingSpacing1200 : tokens.SizingSpacing800
    const hasTenantList =
        valuation?.valuation_type === 'einwert'
            ? (valuation.economic_units?.[0]?.structures?.some((structure) => (structure.tenantsV3 ?? []).length > 0) ?? false)
            : (valuation?.economic_units?.[0]?.tenantsV3?.length ?? 0) > 0
    const isValuationValXMLExportable = valuation?.capabilities?.valxml_exportable.valxml_v1_1

    return (
        <Stack direction="column" minHeight="100%" bgcolor={tokens.ColorBackgroundDark} overflow="hidden">
            <Stack direction="column" gap={tokens.SizingSpacing300}>
                <PageGridContainer background={`linear-gradient(${tokens.ColorBackgroundLight} 235.5px, transparent 235.5px)`}>
                    <PageGridItem columnSpan={6} columnSpanXs={4}>
                        <Stack direction="column" gap={tokens.SizingSpacing400}>
                            <Stack
                                direction="row"
                                paddingTop={tokens.SizingSpacing200}
                                paddingBottom={tokens.SizingSpacing300}
                                gap={tokens.SizingSpacing300}
                                width="100%"
                                alignItems="flex-end"
                            >
                                <Stack direction="column" flexGrow={1} gap={tokens.SizingSpacing300}>
                                    {breadcrumbs}
                                    <Stack direction="row">
                                        <Stack direction="column" gap={tokens.SizingSpacing300} width="100%">
                                            <Stack
                                                direction="row"
                                                gap={tokens.SizingSpacing150}
                                                alignItems="flex-start"
                                                justifyContent="space-between"
                                                width="100%"
                                            >
                                                <Stack direction="column" flexGrow={1}>
                                                    <Typography variant="textM" color={tokens.ColorTextSubtle}>
                                                        {tValuationPageLayout('sectionLabel')}
                                                    </Typography>
                                                    <Stack direction="column" gap={tokens.SizingSpacing100}>
                                                        <Stack direction="row" gap={tokens.SizingSpacing100} alignItems="center">
                                                            <Headline variant="headline1" title={valuationTitle} fullWidth={false} />
                                                            {valuationOptions.length > 1 ? (
                                                                <ContentChipWithOptions
                                                                    color={valuation?.is_draft ? 'orange' : 'green'}
                                                                    label={valuation?.is_draft ? draftTitle : tCommon('final')}
                                                                    options={valuationOptions.map((valuationOption) => ({
                                                                        title: valuationOption.valuationVersion ?? '',
                                                                        callback: () =>
                                                                            navigateToNewValuationPage(valuationOption.valuationId),
                                                                    }))}
                                                                    selectedOption={
                                                                        valuationOptions.find(
                                                                            (option) => option.valuationId === valuation?.uuid
                                                                        )?.valuationVersion
                                                                    }
                                                                />
                                                            ) : (
                                                                <>
                                                                    <ContentChip
                                                                        size="medium"
                                                                        highContrast
                                                                        color={valuation?.is_draft ? 'orange' : 'green'}
                                                                        label={valuation?.is_draft ? draftTitle : tCommon('final')}
                                                                    />
                                                                </>
                                                            )}
                                                        </Stack>
                                                        <Stack
                                                            direction="row"
                                                            gap={tokens.SizingSpacing100}
                                                            maxWidth="max(460px, calc(100vw - 520px))"
                                                        >
                                                            <TextWithEllipsis
                                                                tooltipText={`${addressLine} ${customerAssetReference}`}
                                                                typographyProps={{ variant: 'headline2', color: tokens.ColorTextBoldest }}
                                                            >
                                                                {addressLine}
                                                                {customerAssetReference && (
                                                                    <Box
                                                                        display="inline-block"
                                                                        marginLeft={tokens.SizingSpacing100}
                                                                        sx={{ verticalAlign: 'bottom' }}
                                                                    >
                                                                        <Typography variant="headline3" color={tokens.ColorTextDefault}>
                                                                            {customerAssetReference}
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                            </TextWithEllipsis>
                                                        </Stack>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                            <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="center" height="32px">
                                                {isOrderInitialLoading || isCustomerUsersPending ? (
                                                    <LoadingSkeleton variant="rounded" height="32px" width="500px" />
                                                ) : (
                                                    <>
                                                        <Stack direction="row" gap={tokens.SizingSpacing100} alignItems="center">
                                                            <CalendarDotIcon color={tokens.ColorIconBoldest} size={tokens.SizingIconM} />

                                                            <Typography variant="textS" color={tokens.ColorTextBold} noWrap>
                                                                {valuationDate}
                                                            </Typography>
                                                        </Stack>
                                                        {valuation?.appraiser_assignment && (
                                                            <AppraiserAssignment
                                                                appraiserAssignment={valuation.appraiser_assignment}
                                                                externalAppraisingEntityName={
                                                                    valuation.valuation_type !== 'einwert'
                                                                        ? valuation.appraising_entity_name
                                                                        : undefined
                                                                }
                                                                users={appraiserUsers}
                                                            />
                                                        )}
                                                        <UserLinkList users={assignedCustomerUsers} copy={userLinkCopy} />
                                                    </>
                                                )}
                                            </Stack>
                                        </Stack>
                                        <Stack direction="column" minHeight="100%" gap={headerActionGap}>
                                            {valuation?.report_file && (
                                                <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="center">
                                                    <Typography variant="textS" color={tokens.ColorTextDefault}>
                                                        {tExport('sectionTitle')}
                                                    </Typography>
                                                    <ExportActionButton
                                                        leftIcon={<FilePdfIcon />}
                                                        rightIcon={<DownloadSimpleIcon />}
                                                        label={valuation?.is_draft ? tCommon('draft') : tCommon('report')}
                                                        onClick={() => downloadReport({ valuationId: valuation.uuid })}
                                                        disabled={isDownloadingReport}
                                                    />
                                                    {valuation && (
                                                        <ExportActionButton
                                                            leftIcon={<MicrosoftExcelLogoIcon />}
                                                            rightIcon={<DownloadSimpleIcon />}
                                                            label={tCommon('valXml')}
                                                            onClick={() => setIsValXmlModalOpen(true)}
                                                            disabled={!isValuationValXMLExportable || isDownloadingValXml}
                                                            tooltip={
                                                                !isValuationValXMLExportable
                                                                    ? tExport('valxmlUnavailableTooltip')
                                                                    : undefined
                                                            }
                                                        />
                                                    )}
                                                </Stack>
                                            )}
                                            {isHeaderNavButtonsLoading ? (
                                                <ValuationPageNavButtonsLoad flexGrow={1} />
                                            ) : (
                                                <Stack
                                                    direction="column"
                                                    alignItems="flex-end"
                                                    flexGrow={1}
                                                    minHeight={headerNavButtonMinHeight}
                                                >
                                                    {shouldShowPlausibilityButton &&
                                                        (isNewestDraft ? (
                                                            <TextNavButtonNew
                                                                label={tActions('viewPlausibility')}
                                                                leftIcon={<HighlighterIcon />}
                                                                rightIcon={<ArrowUpRightIcon />}
                                                                href={`/${generatePath(DYNAMIC_APP_PATHS.valuationPlausibilityPage, {
                                                                    assetId: valuation?.asset_uuid,
                                                                    valuationId: valuation?.uuid,
                                                                })}`}
                                                            />
                                                        ) : (
                                                            <TinyNavButton
                                                                label={tActions('viewPlausibility')}
                                                                leftIcon={<HighlighterIcon />}
                                                                rightIcon={<ArrowUpRightIcon />}
                                                                href={`/${generatePath(DYNAMIC_APP_PATHS.valuationPlausibilityPage, {
                                                                    assetId: valuation?.asset_uuid,
                                                                    valuationId: valuation?.uuid,
                                                                })}`}
                                                            />
                                                        ))}
                                                    <TinyNavButton
                                                        label={tActions('viewAsset')}
                                                        leftIcon={<BuildingOfficeIcon />}
                                                        rightIcon={<ArrowUpRightIcon />}
                                                        href={`/assets/${valuation?.asset_uuid}`}
                                                    />
                                                    {shouldShowOrderButton && (
                                                        <TinyNavButton
                                                            label={tActions('viewOrder')}
                                                            leftIcon={<FilesIcon />}
                                                            rightIcon={<ArrowUpRightIcon />}
                                                            href={`/orders/${valuation?.order_uuid}/overview`}
                                                        />
                                                    )}
                                                </Stack>
                                            )}
                                        </Stack>
                                    </Stack>
                                </Stack>

                                <TitleImage
                                    width={255}
                                    height={170}
                                    image={titleImageSrc ? { src: titleImageSrc, alt: tCommon('assetImageAlt') } : undefined}
                                    fallbackImageSrc={[asset?.title_image].filter(isNotNullOrUndefined)}
                                />
                            </Stack>

                            <TabBar selectedTab={selectedTab}>
                                <NavTab label={tCommon('overview')} value="overview" href="overview" />
                                {valuation?.selected_valuation_methods?.includes('income') && (
                                    <NavTab label={tCommon('valuationMethods.income')} value="income" href="income" />
                                )}
                                {valuation?.selected_valuation_methods?.includes('discounted_cashflow') && (
                                    <NavTab label={tCommon('dcf')} value="dcf" href="dcf" />
                                )}
                                {hasTenantList && <NavTab label={tCommon('tenantList')} value="tenant-list" href="tenant-list" />}
                                {isComparablesEnabled && <NavTab label={tCommon('marketData')} value="market-data" href="market-data" />}
                            </TabBar>
                        </Stack>
                    </PageGridItem>
                </PageGridContainer>

                <Outlet />
            </Stack>
            <Footer withTopSpace />
            <ValXmlDownloadModal
                isOpen={isValXmlModalOpen}
                onClose={() => setIsValXmlModalOpen(false)}
                onDownload={async () => {
                    if (!valuation?.uuid) {
                        return
                    }
                    await downloadValXml({ valuationId: valuation.uuid })
                    setIsValXmlModalOpen(false)
                }}
                isDownloading={isDownloadingValXml}
                mode="single"
                isDraft={valuation?.is_draft}
            />
        </Stack>
    )
}
